import { customElement, scss } from '../../core/ids-decorators';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import { attributes, htmlAttributes } from '../../core/ids-attributes';
import { checkOverflow } from '../../utils/ids-dom-utils/ids-dom-utils';

import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsKeyboardMixin from '../../mixins/ids-keyboard-mixin/ids-keyboard-mixin';
import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import IdsElement from '../../core/ids-element';
import styles from './ids-tag.scss';
import {
  applyTextColorValue, IdsColorValue, IdsColorValueCategories, isValidColor
} from '../../utils/ids-color-utils/ids-color-utils';

const Base = IdsLocaleMixin(
  IdsKeyboardMixin(
    IdsEventsMixin(
      IdsElement
    )
  )
);

/**
 * IDS Tag Component
 * @type {IdsTag}
 * @inherits IdsElement
 * @mixes IdsEventsMixin
 * @mixes IdsKeyboardMixin
 * @part tag - the tag element
 * @part icon - the icon element
 * @fires beforetagremove Fires before the tag is removed, allowing you to prevent the removal.
 * Event detail contains `elem` (the tag element) and provides a callback mechanism for vetoing the action
 * @fires tagremove Fires at the time the tag is removed. Detail contains the element `elem`
 * @fires aftertagremove Fires after the tag is removed from the DOM. Detail contains the element `elem`
 */
@customElement('ids-tag')
@scss(styles)
export default class IdsTag extends Base {
  constructor() {
    super();
  }

  /**
   * Invoked each time the custom element is appended into a document-connected element.
   */
  connectedCallback() {
    super.connectedCallback();
    this.#setContainerColor(this.color || '');
    this.#addDimissibleIcon();
    this.#attachEventHandlers();
    this.#attachKeyboardListeners();
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes in an array
   */
  static get attributes(): Array<string> {
    return [
      ...super.attributes,
      attributes.COLOR,
      attributes.TEXT_COLOR,
      attributes.CLICKABLE,
      attributes.DISMISSIBLE,
      attributes.DISABLED,
      attributes.HEIGHT,
      attributes.TEXT_ELLIPSIS,
      attributes.WIDTH
    ];
  }

  /**
   * Create the Template for the contents
   * @returns {string} The template
   */
  template(): string {
    const customClasses: string[] = [];

    this.classList.forEach((value: string) => {
      const expectedCustomClasses = ['calendar-counter'];

      if (expectedCustomClasses.includes(value)) customClasses.push(value);
    });

    return `<span class="ids-tag ${customClasses.join(' ')}" part="tag"><slot></slot></span>`;
  }

  /**
   * Set the tag color
   * @param {IdsColorValue} value The color to use between
   * secondary (white), error, success, warning, caution, info or a hex code with the # at the start
   */
  set color(value: IdsColorValue | IdsColorValueCategories | string) {
    if (value) {
      this.setAttribute(attributes.COLOR, value);
    } else {
      this.removeAttribute(attributes.COLOR);
    }
    this.#setContainerColor(value || '');
    this.#addDimissibleIcon();
  }

  get color(): IdsColorValue | IdsColorValueCategories | string {
    return this.getAttribute(attributes.COLOR) as IdsColorValue;
  }

  /**
   * Set the text color of the tag component
   * @param {IdsColorValue | IdsColorValueCategories | string} value
   * Use design tokens like foreground-dark-default or hex color code starting with '#'.
   */
  set textColor(value: IdsColorValue | IdsColorValueCategories | string) {
    if (!value) {
      this.removeAttribute(attributes.TEXT_COLOR);
      this.container?.style.removeProperty('color');
      return;
    }

    if (typeof value === 'string' && value !== '') {
      if (isValidColor(value)) {
        this.setAttribute(attributes.TEXT_COLOR, value);
        applyTextColorValue(value, this.container as HTMLElement);
      } else {
        this.removeAttribute(attributes.TEXT_COLOR);
        this.container?.style.removeProperty('color');
        return;
      }
    }
    this.#setTextColor(value || '');
  }

  get textColor(): IdsColorValue | IdsColorValueCategories | string {
    return this.getAttribute(attributes.TEXT_COLOR) as IdsColorValue;
  }

  /**
   * Set the text ellipsis on for overflow
   * @param {boolean | string} value If true the tag has ellipsis
   */
  set textEllipsis(value: boolean | string) {
    const val = stringToBool(value);
    if (val) {
      this.setAttribute(attributes.TEXT_ELLIPSIS, '');
      if (checkOverflow(this.container)) {
        this.container?.classList.add('ellipsis');
      }
    } else {
      this.container?.classList.remove('ellipsis');
      this.removeAttribute(attributes.TEXT_ELLIPSIS);
    }
  }

  get textEllipsis(): boolean | string {
    return stringToBool(this.getAttribute(attributes.TEXT_ELLIPSIS));
  }

  /**
   * Set the tag color on the element style
   * @param {string} value The color value
   */
  #setContainerColor(value: string) {
    if (this.container) {
      this.container?.classList.remove('secondary', 'info', 'success', 'warning', 'error');
      this.container.style.backgroundColor = '';
      this.container.style.borderColor = '';
      this.container.style.color = '';

      if (value) {
        if (value.substring(0, 1) === '#') {
          this.container.style.backgroundColor = value;
          this.container.style.borderColor = value;
        } else {
          this.container.classList.add(value);
          if (value !== 'secondary' && value !== 'caution') {
            this.container.classList.add('ids-white');
          }
        }
        this.#setTextColor(this.textColor || '');
      }
    }
  }
  /**
   * Set the Text color on the element style
   * @param {string} value The Text color value
   */

  #setTextColor(value: string) {
    if (this.container) {
      if (value.substring(0, 1) === '#') {
        this.container!.style.color = value;
      } else if (isValidColor(value)) {
        if (['error', 'warning', 'caution', 'success', 'info'].includes(value)) {
          this.container.style.color = `var(--ids-color-${value}-default)`;
        } else if (['white', 'black'].includes(value)) {
          this.container.style.color = `var(--ids-color-${value})`;
        } else if (/^[a-z]+-\d{2,3}$/.test(value)) {
          this.container.style.color = `var(--ids-color-${value})`;
        } else {
          this.container.style.color = `var(--ids-color-${value})`;
        }
      } else {
        this.container!.style.removeProperty('color');
      }
    }
  }

  /**
   * Check if an icon exists if not add it
   * @param {string} iconName The icon name to check
   * @private
   */
  #appendIcon(iconName: string) {
    const icon = this.querySelector(`[${attributes.ICON}="${iconName}"]`);
    if (!icon) {
      this.insertAdjacentHTML(
        'beforeend',
        `<ids-icon part="${attributes.ICON}" ${attributes.ICON}="${iconName}" ${attributes.SIZE}="small" class="ids-icon"></ids-icon>`
      );
      this.#attachEventHandlers();
    }
  }

  /**
   * Check if an icon exists if so, remove it
   * @param {string} iconName The icon name to check
   * @private
   */
  #removeIcon(iconName: string) {
    const icon = this.querySelector(`[${attributes.ICON}="${iconName}"]`);
    if (icon) {
      icon.remove();
    }
  }

  /**
   * If set to true the tag has an x to dismiss
   * @param {boolean} value true of false depending if the tag is dismissed
   */
  set dismissible(value: boolean) {
    const isDismissible = stringToBool(value);
    if (isDismissible) {
      this.setAttribute(attributes.DISMISSIBLE, value.toString());
    } else {
      this.removeAttribute(attributes.DISMISSIBLE);
    }

    this.#addDimissibleIcon();
  }

  get dismissible(): boolean {
    return stringToBool(this.getAttribute(attributes.DISMISSIBLE));
  }

  /**
   * Sets the tag to disabled state so it cannot be dismissed or clicked
   * @param {boolean | string} value true of false
   */
  set disabled(value: boolean | string) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.DISABLED, '');
      this.#updateFocusableState();
    } else {
      this.removeAttribute(attributes.DISABLED);
      this.#updateFocusableState();
    }
  }

  get disabled(): boolean | string {
    return this.hasAttribute(attributes.DISABLED);
  }

  /**
   * Update focusable state based on disabled
   * @private
   */
  #updateFocusableState() {
    const isDisabled = this.disabled;

    if (isDisabled) {
      this.container?.setAttribute(attributes.TABINDEX, '-1');
    } else if (this.dismissible || this.clickable) {
      this.container?.setAttribute(attributes.TABINDEX, '0');
    }
  }

  /**
   * Add the dismissible icon if the tag is dismissible
   * @private
   */
  #addDimissibleIcon() {
    if (this.dismissible) {
      this.container?.classList.add(attributes.FOCUSABLE);
      this.container?.setAttribute(attributes.TABINDEX, this.disabled ? '-1' : '0');
      const tagText = this.textContent?.trim() || 'Tag';
      this.container?.setAttribute(htmlAttributes.ARIA_LABEL, `${tagText}, press Delete to remove`);
      this.#appendIcon('close');
      this.#attachKeyboardListeners();
    } else {
      this.#removeIcon('close');
      this.container?.classList.remove(attributes.FOCUSABLE);
      this.container?.removeAttribute(attributes.TABINDEX);
      this.container?.removeAttribute(htmlAttributes.ARIA_LABEL);
    }
  }

  /**
   * If set to true the tag has focus state and becomes a clickable link
   * @param {boolean} value true or false depending if the tag is clickable
   */
  set clickable(value: boolean) {
    const isClickable = stringToBool(value);
    if (isClickable) {
      this.setAttribute(attributes.CLICKABLE, value.toString());
    } else {
      this.removeAttribute(attributes.CLICKABLE);
    }

    if (isClickable) {
      this.#appendIcon('caret-right');
      this.container?.classList.add(attributes.FOCUSABLE);
      this.container?.setAttribute(attributes.TABINDEX, this.disabled ? '-1' : '0');
      this.#attachKeyboardListeners();
    } else {
      this.#removeIcon('caret-right');
      this.container?.removeAttribute(attributes.TABINDEX);
      this.container?.classList.remove(attributes.FOCUSABLE);
    }
  }

  /**
   * Gets clickable state
   * @returns {boolean} true or false depending if the tag is clickable
   */
  get clickable(): boolean {
    return stringToBool(this.getAttribute(attributes.CLICKABLE));
  }

  /**
   * Set the width of the tag
   * @param {string | null} value The width value (e.g., '100px', '50%')
   */
  set width(value: string | null) {
    if (value) {
      this.setAttribute(attributes.WIDTH, value);
      this.container?.style.setProperty('width', value);
    } else {
      this.removeAttribute(attributes.WIDTH);
      this.container?.style.removeProperty('width');
    }
  }

  get width(): string | null {
    return this.getAttribute(attributes.WIDTH);
  }

  /**
   * Set the height of the tag
   * @param {string | null} value The height value (e.g., '30px', '2rem')
   */
  set height(value: string | null) {
    if (value) {
      this.setAttribute(attributes.HEIGHT, value);
      this.container?.style.setProperty('height', value);
    } else {
      this.removeAttribute(attributes.HEIGHT);
      this.container?.style.removeProperty('height');
    }
  }

  get height(): string | null {
    return this.getAttribute(attributes.HEIGHT);
  }

  /**
   * Establish Internal Event Handlers
   * @private
   * @returns {object} The object for chaining.
   */
  #attachEventHandlers(): this {
    // Handle Clicking the x for dismissible
    const closeIcon = this.querySelector(`ids-icon[${attributes.ICON}="close"]`);
    if (closeIcon) {
      this.offEvent('click', closeIcon);
      this.onEvent('click', closeIcon, () => {
        if (!this.disabled) {
          this.dismiss();
        }
      });
    }

    // Ensure icon is always last
    let isChanging = false;
    this.offEvent('slotchange', this.shadowRoot?.querySelector('slot'));
    this.onEvent('slotchange', this.shadowRoot?.querySelector('slot'), () => {
      if (this.dismissible && !isChanging && this.lastElementChild?.nodeName !== 'IDS-ICON') {
        isChanging = true;
        this.#removeIcon('close');
        this.#appendIcon('close');
        isChanging = false;
      }
    });
    return this;
  }

  /**
   * Establish Internal Keyboard shortcuts
   * @private
   * @returns {object} This API object for chaining
   */
  #attachKeyboardListeners(): object {
    if (this.dismissible) {
      this.listen(['Delete', 'Backspace', 'Enter', ' '], this, () => {
        if (!this.disabled) {
          this.dismiss();
        }
      });
    }

    if (this.clickable) {
      this.listen('Enter', this, () => {
        this.click();
      });
    }

    return this;
  }

  /**
   * Remove the tag from the page
   */
  dismiss() {
    if (!this.dismissible) {
      return;
    }

    let canDismiss = true;
    const response = (veto: any) => {
      canDismiss = !!veto;
    };
    this.triggerEvent('beforetagremove', this, { bubbles: true, detail: { elem: this, response } });

    if (!canDismiss) {
      return;
    }

    this.triggerEvent('tagremove', this, { bubbles: true, detail: { elem: this } });
    this.triggerEvent('aftertagremove', this, { bubbles: true, detail: { elem: this } });
    this.remove();
  }
}
